import { Link } from 'react-router';
import { ORDER_STATE, publicApi, type ApiReference } from '@p1plug/platform';
import { DataTable, Notice } from '@p1plug/ui';
import { C, DocPage, H2, P } from '../doc';

const MODEL: { name: string; what: string; why: string }[] = [
  {
    name: 'Order',
    what: 'Your request to equip one customer at one address.',
    why: 'Idempotent on your own order number. Creates the customer, property and meter point.',
  },
  {
    name: 'Meter point',
    what: 'One customer’s electricity meter, as a stable resource.',
    why: 'The handle for telemetry. It survives device replacement, so your integration never changes IDs.',
  },
  {
    name: 'Installation',
    what: 'One device attached to one meter point, for a period.',
    why: 'Numbered per meter point (1, 2, …). A replacement opens the next one.',
  },
  {
    name: 'Device',
    what: 'A physical P1Plug with a serial number.',
    why: 'Replaceable. Read it for support, never key your data on it.',
  },
  {
    name: 'Event',
    what: 'Something that happened: an order moved, a device activated, a reading arrived.',
    why: 'Delivered by webhook and readable by cursor. Same log, same order.',
  },
  {
    name: 'Access grant',
    what: 'A consumer’s consent to share a meter point’s data with another organisation.',
    why: 'The recipient sees pseudonymous telemetry only, and only while the grant is active.',
  },
];

const STATUS_MAP: [string, keyof typeof ORDER_STATE, string][] = [
  ['requested', 'received', 'Accepted; being validated.'],
  ['awaiting_device', 'ready_for_fulfilment', 'Validated; waiting for a device to be picked.'],
  ['device_assigned', 'processing', 'A device is linked to the order.'],
  ['packed', 'processing', 'The parcel is ready.'],
  ['shipped', 'shipped', 'With the carrier; tracking_code is set.'],
  ['delivered', 'delivered', 'Delivered; waiting for your customer to plug it in.'],
  ['activated', 'activated', 'The device connected and data flows.'],
  ['cancelled', 'cancelled', 'Cancelled before activation.'],
  ['replacement_in_progress', 'processing', 'A replacement device is on its way to the same meter point.'],
];

export function Concepts() {
  return (
    <DocPage
      title="Concepts"
      lead="Six resources carry the whole integration. Knowing which one is stable saves you a migration later."
    >
      <DataTable
        rowKey={(m) => m.name}
        columns={[
          { key: 'name', label: 'Resource' },
          { key: 'what', label: 'What it is' },
          { key: 'why', label: 'What to know' },
        ]}
        rows={MODEL}
      />

      <H2>Key your data on the meter point</H2>
      <P>
        Devices fail, get lost and are replaced. If your data is keyed on a device ID or serial, every replacement
        becomes a data migration. Key it on <C>meter_point_id</C> (or on your own customer reference, which the order
        links to it) and a replacement is a single <C>installation.replaced</C> event that changes nothing in your
        model.
      </P>

      <H2>Order statuses</H2>
      <P>
        The API reports the detailed status. P1Plug’s own interfaces group them into the states in the middle column;
        you are free to do the same.
      </P>
      <DataTable
        rowKey={(r) => r[0]}
        columns={[
          { key: 'status', label: 'status in the API', mono: true, render: (r) => r[0] },
          { key: 'state', label: 'Shown as', render: (r) => ORDER_STATE[r[1]].label },
          { key: 'meaning', label: 'Meaning', render: (r) => r[2] },
        ]}
        rows={STATUS_MAP}
      />

      <H2>Readings</H2>
      <P>
        A reading is one telegram of the smart meter, typically every 10 seconds on current meters. Each has a sequence
        number per installation. When a device was offline it delivers its buffered readings afterwards, flagged{' '}
        <C>backfill: true</C> and with their original <C>event_time</C>; order your own storage by <C>event_time</C>,
        not by arrival.
      </P>
      <P>
        Cumulative counters (<C>energy_import_t1_wh</C> …) come straight from the meter. Compute consumption over a
        period as the difference between two readings; do not sum power.
      </P>

      <H2>Your customers do not see P1Plug</H2>
      <P>
        P1Plug usually works underneath your brand. Nothing in the API requires your customer to know P1Plug exists,
        create an account or install an app: the device is linked to them by your order, and activates when it is
        plugged in.
      </P>
      <P>
        Next: <Link to="/docs/authentication">Authentication</Link>.
      </P>
    </DocPage>
  );
}

export function Environments() {
  return (
    <DocPage
      title="Environments"
      lead="Sandbox and production are separate worlds: separate URLs, separate credentials, separate data."
    >
      <DataTable
        rowKey={(r) => r.name}
        columns={[
          { key: 'name', label: 'Environment' },
          { key: 'purpose', label: 'For' },
          { key: 'devices', label: 'Devices' },
          { key: 'data', label: 'Data' },
        ]}
        rows={[
          {
            name: 'Sandbox',
            purpose: 'Building and testing your integration',
            devices: 'Synthetic: you create and control them',
            data: 'Synthetic readings; safe to reset',
          },
          {
            name: 'Production',
            purpose: 'Your customers',
            devices: 'Physical P1Plugs in homes',
            data: 'Personal data; contractual retention',
          },
        ]}
      />
      <H2>Rules that hold across environments</H2>
      <ul className="p1x-prose">
        <li>
          The API contract is identical. Code that works in the sandbox works in production with a different base URL
          and credential.
        </li>
        <li>
          A credential belongs to exactly one environment. A sandbox credential against production answers <C>401</C>,
          never data.
        </li>
        <li>Identifiers never cross: an order created in the sandbox does not exist in production.</li>
        <li>Each environment has its own webhook endpoint and its own event log and cursors.</li>
      </ul>
      <Notice tone="info" title="Where this stands today">
        The platform currently runs as a single test environment per installation, with virtual devices operated by
        P1Plug. The separation above is how the sandbox and production are being built; your code does not need to
        change when it arrives, only your base URL and credentials.
      </Notice>
    </DocPage>
  );
}

const api = publicApi as ApiReference;

export function Changelog() {
  const rows = [
    {
      version: `API ${api.version}`,
      date: '7 October 2026',
      text: 'OAuth 2.0 client credentials with scopes. Event log with cursor replay (GET /api/v1/events). Data sharing through access grants. Webhook event-type filters.',
    },
    {
      version: 'SDK 0.3.0 (TypeScript)',
      date: '7 October 2026',
      text: 'OAuth support with token caching, events.list and events.iterate, access grants.',
    },
    {
      version: 'API 1.1',
      date: '7 October 2026',
      text: 'Orders, meter points, installations, webhooks with signed batches, idempotent order creation.',
    },
    { version: 'API 1.0', date: '7 October 2026', text: 'Device and telemetry reads.' },
  ];
  return (
    <DocPage
      title="Changelog"
      lead="Changes to the API contract and the SDK. Within /api/v1, changes are additive: new fields and operations appear, existing ones keep their meaning."
    >
      <DataTable
        rowKey={(r) => r.version}
        columns={[
          { key: 'version', label: 'Version' },
          { key: 'date', label: 'Date' },
          { key: 'text', label: 'What changed' },
        ]}
        rows={rows}
      />
      <P>
        Ignore fields you do not know and you will not notice a new version. A breaking change would be a new path (
        <C>/api/v2</C>) and a new event <C>schema_version</C>, announced here first.
      </P>
    </DocPage>
  );
}
